import { PackBox, PackInfo } from "../PackBox";
import { FC } from "react";
import { IItemGroup } from "../../ScannerApp";
import getDate from "../../getDate";
import { countPieces, statusAr, t } from "../../i18n/ar";
import { IOrderResponse } from "../../interfaces/responses/OrderResponse.interface";
import { StockChip } from "../packing/StockChip";
import { SkuLink } from "../SkuLink";

// One card per DISTINCT product. Reading order for the worker:
//   1. big number badge  (which product am I on: 1 of 2)
//   2. the picture        (what does it look like)
//   3. giant ×N           (how many pieces to take)
//   4. SKU in huge mono   (match against the shelf label)
// Everything else is folded away under "تفاصيل".
export const Product: FC<{
    group: IItemGroup;
    order: IOrderResponse;
    index: number;
    total: number;
    // Counted warehouse units for this SKU (undefined = no info)
    stock?: number | null;
    // jumia.ma product page of this SKU (undefined = unknown)
    url?: string;
    groupName?: string;
    variation?: string;
    pack?: PackInfo;
}> = ({ group, order, index, total, stock, url, groupName, variation, pack }) => {
    const { isRts, units, sku, name, imageUrl, status } = group;
    const qty = units.length;
    const first = units[0];
    const multi = total > 1;

    const details = [
        { label: t.price, value: first.itemPriceLocal != null ? `${first.itemPriceLocal} MAD` : "" },
        { label: t.tracking, value: String(first.trackingNumber || "") },
        { label: t.shipment, value: String(first.shipmentType || "") },
        { label: t.createdAt, value: getDate(new Date(order.createdAt.toString())) },
        { label: t.updatedAt, value: getDate(new Date(order.updatedAt.toString())) },
    ].filter((d) => d.value);

    return (
        <div
            className={`overflow-hidden rounded-3xl bg-white shadow-lg ${
                isRts ? "border-4 border-emerald-500" : "border-4 border-red-500 opacity-90"
            }`}
        >
            {/* Header bar: pack / don't pack */}
            <div
                className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white ${
                    isRts ? "bg-emerald-600" : "bg-red-600"
                }`}
            >
                <div className="flex items-center gap-3">
                    {multi && (
                        <span className="ltr flex h-14 w-14 items-center justify-center rounded-full bg-white text-3xl font-black text-slate-900 shadow">
                            {index + 1}
                        </span>
                    )}
                    <span className="text-3xl font-black sm:text-4xl">
                        {isRts ? `✅ ${t.pack}` : `⛔ ${t.dontPack}`}
                    </span>
                </div>
                <span className="rounded-lg bg-black/20 px-3 py-1 text-lg font-bold">{statusAr(status)}</span>
            </div>

            <div className="flex flex-col gap-5 p-4 sm:flex-row sm:p-5">
                {/* Picture */}
                <a
                    href={imageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 self-center sm:self-start"
                    title={name}
                >
                    <img
                        src={imageUrl}
                        alt=""
                        className="h-80 w-80 rounded-2xl border border-slate-200 bg-white object-contain sm:h-72 sm:w-72 lg:h-80 lg:w-80"
                    />
                </a>

                <div className="flex min-w-0 flex-1 flex-col gap-4">
                    {/* Quantity — the most important number on the card */}
                    <div
                        className={`flex items-center justify-between gap-3 rounded-2xl px-4 py-3 ${
                            qty > 1
                                ? "bg-amber-100 ring-4 ring-amber-500"
                                : isRts
                                ? "bg-emerald-50"
                                : "bg-red-50"
                        }`}
                    >
                        <div className="flex items-baseline gap-3">
                            <span className="ltr text-7xl font-black leading-none text-slate-900">×{qty}</span>
                            <span className="text-2xl font-bold text-slate-700">{countPieces(qty)}</span>
                        </div>
                        <div className="text-4xl leading-none" aria-hidden>
                            {Array.from({ length: Math.min(qty, 6) }).map((_, i) => (
                                <span key={i}>📦</span>
                            ))}
                            {qty > 6 && <span className="text-2xl font-black">…</span>}
                        </div>
                    </div>

                    {/* SKU — matches the shelf label, so it must be big and unambiguous */}
                    <div className="rounded-2xl border-2 border-slate-300 bg-slate-50 px-4 py-3">
                        <p className="text-sm font-bold text-slate-500">🏷️ {t.sku}</p>
                        <p className="ltr break-all font-mono text-3xl font-black tracking-wide text-slate-900 sm:text-4xl">
                            <SkuLink sku={sku} url={url} />
                        </p>
                    </div>

                    {pack && <PackBox pack={pack} units={group.units?.length || 1} />}
                    {variation && <p className="ltr inline-flex items-center gap-2 rounded-xl border-2 border-amber-400 bg-amber-100 px-3 py-1 text-2xl font-black text-amber-900">📏 <span className="text-base font-bold text-amber-700">المقاس / اللون</span> {variation}</p>}
                    {groupName && <p className="ltr inline-block rounded-lg bg-indigo-50 px-2.5 py-1 text-start text-lg font-black uppercase tracking-wide text-indigo-900">📦 {groupName}</p>}
                    <p className="ltr text-start text-lg font-bold leading-snug text-slate-700">{name}</p>

                    {/* Warehouse stock hint — only for pieces that must be packed */}
                    {isRts && stock !== undefined && (
                        <div><StockChip stock={stock} needed={qty} big /></div>
                    )}

                    {first.history?.afterSalesDetails?.reason && (
                        <p className="rounded-xl bg-red-50 px-3 py-2 text-base font-bold text-red-700">
                            {t.reason}: <span className="ltr">{String(first.history.afterSalesDetails.reason)}</span>
                        </p>
                    )}

                    <details className="group rounded-xl border border-slate-200 bg-white">
                        <summary className="cursor-pointer select-none px-4 py-2 text-base font-bold text-slate-500 hover:bg-slate-50">
                            ℹ️ {t.details}
                        </summary>
                        <div className="space-y-1.5 border-t border-slate-100 px-4 py-3 text-sm">
                            {details.map(({ label, value }) => (
                                <div key={label} className="flex items-start gap-2">
                                    <span className="w-28 shrink-0 font-bold text-slate-500">{label}:</span>
                                    <span className="ltr break-all font-medium text-slate-700">{value}</span>
                                </div>
                            ))}
                            {first.trackingUrl && (
                                <div className="flex items-start gap-2">
                                    <span className="w-28 shrink-0 font-bold text-slate-500">{t.trackLink}:</span>
                                    <a
                                        href={first.trackingUrl.toString()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="font-bold text-[#f68b1e] underline underline-offset-2"
                                    >
                                        🔗 {t.trackLink}
                                    </a>
                                </div>
                            )}
                        </div>
                    </details>
                </div>
            </div>
        </div>
    );
};
